import TabsPanels from '@/components/tabs/TabsPanels';

import { DISPENSE_PANEL_ROUTE, REDEMPTION_PANEL_ROUTE } from '@/constants/app/transaction';
import TransactionDetailsTab from './TransactionDetailsTab';
import TransactionDispenseTab from './TransactionDispenseTab';
import TransactionRedemptionTab from './TransactionRedemptionTab';
import TransactionStatusLogTab from './TransactionStatusLogTab';

import type { Tag, TransactionDetail, TransactionImageSource } from '@/types';
import { useMemo } from 'react';
import type { ReactNode } from 'react';

interface TransactionTabsPanelsProps {
  transaction: TransactionDetail;
  images: TransactionImageSource[];
  hideAtmValue: boolean;
  hideCustomerLink: boolean;
  hide: () => void;
  tagIds: string[];
  tagsCollection: Tag[];
  resourceId: string;
  panels: ReactNode[];
  routes: string[];
}

const TransactionTabsPanels = ({
  transaction,
  images,
  hideAtmValue,
  hideCustomerLink,
  hide,
  tagIds,
  tagsCollection,
  resourceId,
  panels,
  routes,
}: TransactionTabsPanelsProps) => {
  // TabsPanels renders each entry as a component type, so a new inline function on every render
  // remounts the panel and wipes its local state (e.g. the redemption tab's selected method).
  // Each panel is memoized separately so a re-render of the drawer doesn't remount them.
  const detailsTab = useMemo(
    () => () => (
      <TransactionDetailsTab
        transaction={transaction}
        images={images}
        hideAtmValue={hideAtmValue}
        hideCustomerLink={hideCustomerLink}
        hide={hide}
        tagIds={tagIds}
        tagsCollection={tagsCollection}
        resourceId={resourceId}
      />
    ),
    [transaction, images, hideAtmValue, hideCustomerLink, hide, tagIds, tagsCollection, resourceId],
  );
  const statusLogTab = useMemo(
    () => () => <TransactionStatusLogTab transaction={transaction} />,
    [transaction],
  );
  const dispenseTab = useMemo(
    () => () => <TransactionDispenseTab transaction={transaction} />,
    [transaction],
  );
  const redemptionTab = useMemo(
    () => () => <TransactionRedemptionTab transaction={transaction} compact hide={hide} />,
    [transaction, hide],
  );

  let components = [detailsTab, statusLogTab];

  if (routes.includes(DISPENSE_PANEL_ROUTE)) {
    components = [...components, dispenseTab];
  }

  if (routes.includes(REDEMPTION_PANEL_ROUTE)) {
    components = [...components, redemptionTab];
  }

  return <TabsPanels name="transactions" panels={panels} routes={routes} components={components} />;
};

export default TransactionTabsPanels;
